import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchWithSecrets } from "../../utils/addressSecrets";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  resetPasswordSchema,
  shownFieldError,
} from "../../utils/validationPatterns";
import useAuth from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { BiCheck, BiX } from "react-icons/bi";
import PasswordValidator from "../../components/public/PasswordValidator";
import { CustomButton } from "../../components/atoms/CustomButton";
import { useCheckPassword } from "../../hooks/auth";
import { useResetPassword } from "../../hooks/users";

const ResetPassword = () => {
  const { t } = useTranslation();

  const auth = useAuth();
  const navigate = useNavigate();
  const searchParams = useSearchWithSecrets();

  const from = "/control-panel";

  useEffect(() => {
    let cleaner = true;

    if (cleaner && auth.user.isAuthenticated) navigate(from);

    if (!searchParams.get("user_identification") && !searchParams.get("token"))
      navigate("/");

    return () => {
      cleaner = false;
    };
  }, [auth, from, navigate, searchParams]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validatorCurrentPassword, setValidatorCurrentPassword] =
    useState<string>("");

  const rules = [
    {
      regex: "^.{8,32}$",
      message: t("ruleValidator.key1", { min: 8, max: 32 }),
    },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(resetPasswordSchema),
  });

  const { mutate: checkPassword } = useCheckPassword();
  const { mutate: resetPassword, isLoading: isResetting } = useResetPassword();

  const onSubmit = (data: any) => {
    checkPassword(
      {
        password: data.new_password,
        level: "level2",
      },
      {
        onSuccess: () => {
          resetPassword(
            {
              user_identification: searchParams.get("user_identification"),
              token: searchParams.get("token"),
              data,
            },
            {
              onSuccess: () => {
                toast.success(t("resetPassword.msg_1"));
                navigate("/login", { replace: true });
              },
            }
          );
        },
      }
    );
  };

  return (
    <div className="auth-page min-vh-100 d-flex align-items-center">
      <Helmet>
        <title>Super User Manager - Reset password</title>
        <meta
          name="description"
          content="Super User Manager - Reset your password"
        />
        <meta name="robots" content="noindex" />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/reset-password`}
        />
      </Helmet>

      <div className="md:w-[448px] w-full flex-col justify-start items-start gap-8 inline-flex">
        <div className="self-stretch flex-col justify-start items-center gap-6 flex">
          <div className="flex-col justify-start items-center gap-2 flex">
            <div className="text-center text-gray-700 text-2xl font-bold font-figtree leading-9">
              {t("resetPassword.title")}
            </div>
            <div className="justify-start items-center gap-1 inline-flex">
              <div className="text-gray-500 text-sm font-normal font-figtree leading-tight">
                {t("resetPassword.text")}
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="self-stretch flex-col justify-start items-start gap-6 flex"
        >
          <div
            className={`self-stretch bg-white rounded-md border ${
              Object.keys(errors).length > 0
                ? "border-red-500"
                : "border-gray-300"
            } flex-col justify-start items-start flex`}
          >
            <div className="self-stretch px-3 py-2 bg-white rounded-tl-md rounded-tr-md shadow justify-between items-center inline-flex">
              <input
                type={showPassword ? "text" : "password"}
                className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
                placeholder={t("resetPassword.label_1").toString()}
                {...register("new_password", {
                  onChange(event) {
                    setValidatorCurrentPassword(event.target.value);
                  },
                })}
              />

              <button
                className="w-5 h-5 relative"
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {!showPassword ? (
                  <HiEye className="text-gray-400" size={20} />
                ) : (
                  <HiEyeOff className="text-gray-400" size={20} />
                )}
              </button>
            </div>

            <div className="self-stretch px-3 py-2 bg-white rounded-bl-md rounded-br-md shadow border-t border-gray-300 justify-between items-center inline-flex">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
                placeholder={t("resetPassword.label_2").toString()}
                {...register("new_confirm_password")}
              />

              <button
                className="w-5 h-5 relative"
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {!showConfirmPassword ? (
                  <HiEye className="text-gray-400" size={20} />
                ) : (
                  <HiEyeOff className="text-gray-400" size={20} />
                )}
              </button>
            </div>

            <PasswordValidator
              className={validatorCurrentPassword.length > 0 ? "p-3" : "hidden"}
              ruleSet={rules}
              passwordValue={validatorCurrentPassword}
              passedRuleClassName="flex items-center text-green-500"
              failedRuleClassName="flex items-center text-red-500"
              passIcon={<BiCheck size={20} />}
              failIcon={<BiX size={20} />}
            />
          </div>

          {Object.keys(errors).length > 0 && (
            <div>
              {Object.keys(errors).map((key) => (
                <p key={key} className="mb-0 text-xs text-red-600">
                  {shownFieldError(t, errors[key]?.message?.toString())}
                </p>
              ))}
            </div>
          )}

          <CustomButton
            type="submit"
            variant="primary"
            disabled={isResetting}
            isLoading={isResetting}
            title="Reset"
          />
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
